import { METAFIELDS_FIELD, toProductDetails, type RawMetafields } from './product.details';
import { readShopifyConfig, storefrontFetch, type ShopifyConfig } from './shopify.client';
import {
  CommerceError,
  type ProductImage,
  type ProductPage,
  type ProductRepository,
  type ProductVariant,
  type SellingPlan,
  type ShopProduct,
} from './shopify.types';

/** Photos rapportées pour la fiche complète : au-delà, personne ne fait défiler. */
const GALLERY_SIZE = 6;

/** Largeur demandée au CDN pour la galerie : une fiche, pas une étagère. */
const GALLERY_WIDTH = 800;

/** Déclinaisons rapportées : au-delà, ce n'est plus un choix, c'est une liste. */
const VARIANTS_SIZE = 8;

const PRODUCT_FIELDS = `
  id
  handle
  title
  description(truncateAt: 200)
  descriptionHtml
  availableForSale
  onlineStoreUrl
  featuredImage { url altText }
  images(first: ${GALLERY_SIZE}) { nodes { url altText } }
  ${METAFIELDS_FIELD}
  variants(first: ${VARIANTS_SIZE}) {
    nodes {
      id
      title
      availableForSale
      price { amount currencyCode }
      compareAtPrice { amount currencyCode }
    }
  }
  sellingPlanGroups(first: 1) {
    nodes {
      name
      sellingPlans(first: 1) { nodes { id name } }
    }
  }
`;

interface RawImage {
  url?: string;
  altText?: string | null;
}

interface RawMoney {
  amount?: string;
  currencyCode?: string;
}

interface RawProduct {
  id?: string;
  handle?: string;
  title?: string;
  description?: string;
  descriptionHtml?: string;
  availableForSale?: boolean;
  onlineStoreUrl?: string | null;
  featuredImage?: RawImage | null;
  images?: { nodes?: (RawImage | null)[] } | null;
  metafields?: RawMetafields;
  variants?: {
    nodes?: {
      id?: string;
      title?: string;
      availableForSale?: boolean;
      price?: RawMoney;
      compareAtPrice?: RawMoney | null;
    }[];
  };
  sellingPlanGroups?: {
    nodes?: {
      name?: string;
      sellingPlans?: { nodes?: { id?: string; name?: string }[] };
    }[];
  };
}

interface RawProductConnection {
  nodes?: (RawProduct | null)[];
  pageInfo?: { hasNextPage?: boolean; endCursor?: string | null };
}

/** Hôtes du CDN Shopify : ailleurs, on ne sait pas redimensionner. */
const SHOPIFY_CDN_HOSTS = ['cdn.shopify.com', 'cdn.shopifycdn.net'];

/**
 * Ajoute `width` à une image du CDN Shopify pour ne pas télécharger une image de
 * 1000 px dans une vignette d'étagère. Les paramètres existants, dont le `?v=`
 * qui sert de clé de cache, sont préservés.
 */
export function resizedImageUrl(url: string, width: number): string {
  if (!url || !Number.isFinite(width) || width <= 0) return url;

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    // URL relative ou malformée : on la rend telle quelle plutôt que de casser l'image.
    return url;
  }

  const host = parsed.hostname.toLowerCase();
  const isShopify = SHOPIFY_CDN_HOSTS.some((cdn) => host === cdn || host.endsWith(`.${cdn}`));
  if (!isShopify) return url;

  parsed.searchParams.set('width', String(Math.round(width)));
  return parsed.toString();
}

/**
 * Validation défensive : un produit sans variante, sans prix ou sans handle est
 * inutilisable. Mieux vaut l'ignorer que d'afficher une fiche à moitié vide.
 */
/**
 * Les fiches Shopify commencent souvent par répéter le titre du produit.
 * On le retire et on garde une phrase courte, comme prévu au §15.
 */
export function shortDescription(description: string, title: string): string {
  let text = description.replace(/\s+/g, ' ').trim();

  // Le préfixe peut être le titre complet ou seulement son début
  // (« Bois de cerf entier » → la fiche commence par « Bois de cerf »).
  const words = title.split(' ');
  for (let length = words.length; length >= 2; length -= 1) {
    const prefix = words.slice(0, length).join(' ');
    if (text.toLowerCase().startsWith(prefix.toLowerCase())) {
      text = text.slice(prefix.length).replace(/^[\s:—–-]+/, '');
      break;
    }
  }
  if (text.length <= 150) return text;
  const cut = text.slice(0, 150);
  const lastStop = Math.max(cut.lastIndexOf('. '), cut.lastIndexOf(' ! '), cut.lastIndexOf(' ? '));
  return lastStop > 60 ? cut.slice(0, lastStop + 1) : `${cut.trimEnd()}…`;
}

/**
 * Galerie de la fiche : les images du produit, redimensionnées pour le CDN.
 * Sans galerie exploitable, on retombe sur l'image principale plutôt que de
 * laisser un carrousel vide.
 */
export function toGallery(
  nodes: (RawImage | null)[] | undefined,
  fallbackUrl: string,
  fallbackAlt: string,
): ProductImage[] {
  const gallery: ProductImage[] = [];
  for (const node of nodes ?? []) {
    const url = node?.url;
    if (!url) continue;
    gallery.push({ url: resizedImageUrl(url, GALLERY_WIDTH), alt: node?.altText ?? fallbackAlt });
  }
  if (gallery.length > 0) return gallery;
  return fallbackUrl ? [{ url: resizedImageUrl(fallbackUrl, GALLERY_WIDTH), alt: fallbackAlt }] : [];
}

/**
 * Déclinaisons achetables. Shopify nomme `Default Title` la variante unique
 * d'un produit sans option : ce libellé ne veut rien dire pour un visiteur, on
 * le remplace par le titre du produit.
 *
 * Le prix barré n'est retenu que s'il est réellement supérieur au prix payé.
 * Une promotion terminée laisse parfois un `compareAtPrice` égal, et afficher
 * « 24,90 € au lieu de 24,90 € » est pire que de ne rien afficher.
 */
export function toVariants(raw: RawProduct, titreProduit: string): ProductVariant[] {
  const variants: ProductVariant[] = [];
  for (const node of raw.variants?.nodes ?? []) {
    const amount = node?.price?.amount;
    if (!node?.id || amount === undefined) continue;

    const barre = node.compareAtPrice?.amount;
    const remise =
      barre !== undefined &&
      Number.parseFloat(barre) > Number.parseFloat(amount)
        ? { amount: barre, currencyCode: node.compareAtPrice?.currencyCode ?? 'EUR' }
        : undefined;

    variants.push({
      id: node.id,
      title: !node.title || node.title === 'Default Title' ? titreProduit : node.title,
      price: { amount, currencyCode: node.price?.currencyCode ?? 'EUR' },
      availableForSale: node.availableForSale === true,
      ...(remise ? { compareAtPrice: remise } : {}),
    });
  }
  return variants;
}

/** Offre d'abonnement, si le produit en publie une. */
export function toSellingPlan(raw: RawProduct): SellingPlan | undefined {
  const groupe = raw.sellingPlanGroups?.nodes?.[0];
  const plan = groupe?.sellingPlans?.nodes?.[0];
  if (!groupe?.name || !plan?.id) return undefined;
  return { id: plan.id, name: groupe.name, cadence: plan.name ?? '' };
}

/**
 * Arguments de vente de la fiche Shopify.
 *
 * Ce sont les `<li>` de la description riche, quand le rédacteur en a mis. Le
 * balisage est celui de l'éditeur Shopify, donc du HTML de confiance côté
 * marchand, mais on n'en garde que le TEXTE : rien n'est réinjecté en HTML,
 * il n'y a donc aucune surface d'injection dans la boutique virtuelle.
 *
 * On s'arrête à six puces : au-delà, ce n'est plus un argumentaire.
 */
export function toHighlights(html: string | undefined): string[] {
  if (!html) return [];
  const puces: string[] = [];
  for (const bloc of html.match(/<li\b[^>]*>[\s\S]*?<\/li>/gi) ?? []) {
    const texte = bloc
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&#39;|&rsquo;/g, '’')
      .replace(/&quot;/g, '"')
      .replace(/\s+/g, ' ')
      .trim();
    if (texte.length >= 3 && texte.length <= 120) puces.push(texte);
    if (puces.length === 6) break;
  }
  return puces;
}

export function toShopProduct(raw: RawProduct | null | undefined): ShopProduct | null {
  if (!raw?.handle || !raw.id || !raw.title) return null;

  const variant = raw.variants?.nodes?.[0];
  const amount = variant?.price?.amount;
  if (!variant?.id || amount === undefined) return null;

  // Un produit dont la variante n'est pas vendable ne doit jamais être ajoutable.
  const availableForSale = raw.availableForSale === true && variant.availableForSale === true;

  const imageUrl = raw.featuredImage?.url ?? '';
  const imageAlt = raw.featuredImage?.altText ?? raw.title;
  const details = toProductDetails(raw.metafields);
  const variants = toVariants(raw, raw.title);
  const abonnement = toSellingPlan(raw);
  const highlights = toHighlights(raw.descriptionHtml);

  return {
    id: raw.id,
    handle: raw.handle,
    title: raw.title,
    description: shortDescription(raw.description ?? '', raw.title),
    imageUrl,
    imageAlt,
    images: toGallery(raw.images?.nodes, imageUrl, imageAlt),
    price: { amount, currencyCode: variant.price?.currencyCode ?? 'EUR' },
    variantId: variant.id,
    availableForSale,
    ...(raw.onlineStoreUrl ? { productUrl: raw.onlineStoreUrl } : {}),
    ...(details ? { details } : {}),
    ...(variants.length ? { variants } : {}),
    ...(abonnement ? { sellingPlan: abonnement } : {}),
    ...(highlights.length ? { highlights } : {}),
  };
}

export class ShopifyProductRepository implements ProductRepository {
  constructor(private readonly config: ShopifyConfig) {}

  async getFeaturedProducts(): Promise<ShopProduct[]> {
    const data = await storefrontFetch<{ products?: { nodes?: RawProduct[] } }>(
      this.config,
      `query Featured { products(first: 4, sortKey: BEST_SELLING) { nodes { ${PRODUCT_FIELDS} } } }`,
    );
    return (data.products?.nodes ?? [])
      .map(toShopProduct)
      .filter((product): product is ShopProduct => product !== null);
  }

  async getProductsByHandles(handles: string[]): Promise<ShopProduct[]> {
    const products = await Promise.all(handles.map((handle) => this.getProductByHandle(handle)));
    return products.filter((product): product is ShopProduct => product !== null);
  }

  async getProductByHandle(handle: string): Promise<ShopProduct | null> {
    const data = await storefrontFetch<{ product?: RawProduct | null }>(
      this.config,
      `query ByHandle($handle: String!) { product(handle: $handle) { ${PRODUCT_FIELDS} } }`,
      { handle },
    );
    return toShopProduct(data.product);
  }

  async getCollectionProducts(
    handle: string,
    first: number,
    after?: string | null,
  ): Promise<ProductPage> {
    const data = await storefrontFetch<{ collection?: { products?: RawProductConnection } | null }>(
      this.config,
      `query CollectionProducts($handle: String!, $first: Int!, $after: String) {
        collection(handle: $handle) {
          products(first: $first, after: $after) {
            nodes { ${PRODUCT_FIELDS} }
            pageInfo { hasNextPage endCursor }
          }
        }
      }`,
      { handle, first, after: after ?? null },
    );

    // Une collection inexistante renvoie `collection: null` sans erreur GraphQL :
    // une étagère vide est une réponse valable, pas une panne.
    const connection = data.collection?.products;
    if (!connection) return { products: [], hasNextPage: false, endCursor: null };

    return {
      products: (connection.nodes ?? [])
        .map(toShopProduct)
        .filter((product): product is ShopProduct => product !== null),
      hasNextPage: connection.pageInfo?.hasNextPage === true,
      endCursor: connection.pageInfo?.endCursor ?? null,
    };
  }
}

export function createShopifyRepository(): ShopifyProductRepository {
  const config = readShopifyConfig();
  if (!config) throw new CommerceError('Shopify n’est pas configuré.', false);
  return new ShopifyProductRepository(config);
}
